import { useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { EChartsInstance } from './echartsCore';
import { TimeRangeContextType } from '../../../context/TimeRangeContext';
import { resolveUserTimezone } from '../../../helpers/timeRangeUtils';
import { PERIOD_CUSTOM } from '../../Constants';

export const resolveSelectedRange = (startMs: number, endMs: number): { from: Dayjs; to: Dayjs; timezone: string } => {
    const timezone = resolveUserTimezone();
    const from = timezone === 'UTC' ? dayjs.utc(startMs) : dayjs(startMs).tz(timezone);
    let to = timezone === 'UTC' ? dayjs.utc(endMs) : dayjs(endMs).tz(timezone);

    if (to.diff(from, 'minute') < 1) {
        to = from.add(1, 'minute');
    }

    return { from, to, timezone };
};

interface BrushArea {
    coordRange?: unknown;
}

const readRange = (areas: BrushArea[] | undefined): [number, number] | null => {
    const range = areas?.[0]?.coordRange;
    if (!Array.isArray(range) || range.length < 2) {
        return null;
    }
    const [start, end] = range;
    if (typeof start !== 'number' || typeof end !== 'number' || start === end) {
        return null;
    }
    return start < end ? [start, end] : [end, start];
};

export const useTimeRangeBrush = (
    chart: EChartsInstance | null,
    timeRangeContext: TimeRangeContextType,
    optionVersion: unknown,
): void => {
    const contextRef = useRef(timeRangeContext);
    contextRef.current = timeRangeContext;

    useEffect(() => {
        if (!chart || chart.isDisposed()) {
            return;
        }

        chart.dispatchAction({
            type: 'takeGlobalCursor',
            key: 'brush',
            brushOption: { brushType: 'lineX', brushMode: 'single' },
        });
    }, [chart, optionVersion]);

    useEffect(() => {
        if (!chart || chart.isDisposed()) {
            return;
        }

        const onBrushEnd = (params: { areas?: BrushArea[] }) => {
            const range = readRange(params.areas);

            chart.dispatchAction({ type: 'brush', command: 'clear', areas: [] });
            chart.dispatchAction({
                type: 'takeGlobalCursor',
                key: 'brush',
                brushOption: { brushType: 'lineX', brushMode: 'single' },
            });

            if (!range) {
                return;
            }

            const { from, to, timezone } = resolveSelectedRange(range[0], range[1]);
            contextRef.current.applyPeriod(PERIOD_CUSTOM, from, to, timezone);
        };

        chart.on('brushEnd', onBrushEnd as never);
        return () => {
            if (!chart.isDisposed()) {
                chart.off('brushEnd', onBrushEnd as never);
            }
        };
    }, [chart]);
};
